import React, { useCallback, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { Select, Radio, DatePicker, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';
import { IDeviceActionRequestPayload } from '../types';

interface IUActionSuspension {
  actionKey: number;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  devicesProps: IDevice[] | undefined;
  uploadedDevices: any;
}

const useActionSuspension = ({
  devicesProps,
  uploadedDevices,
  handleDeviceActions,
}: IUActionSuspension) => {
  const [selectedSuspension, setSelectedSuspension] = useState<number>(30);
  const [dateTimeToRemoveSuspension, setDateTimeToRemoveSuspension] =
    useState<Moment | null>(moment().add(1, 'hour'));
  const [extraAction, setExtraAction] = useState(9);

  const { t: translate } = useTranslation(['device-actions-modal']);

  const suspensionOptions = useMemo(() => {
    return [
      {
        label: translate('steps.contract-suspend.suspend-for', {
          days: 30,
        }),
        value: 30,
      },
      {
        label: translate('steps.contract-suspend.suspend-for', {
          days: 60,
        }),
        value: 60,
      },
      {
        label: translate('steps.contract-suspend.suspend-for', {
          days: 90,
        }),
        value: 90,
      },
      {
        label: translate('steps.contract-suspend.suspend-for', {
          days: 120,
        }),
        value: 120,
      },
    ];
  }, [translate]);

  const disabledDate = (date: Moment) => {
    const inputDate = moment(date);
    const currentDate = moment().startOf('day');

    return inputDate.isBefore(currentDate);
  };

  const contentSteps = [
    {
      title: translate('steps.contract-suspend.name'),
      content: (
        <div>
          <Radio.Group
            name="radiogroup"
            defaultValue={9}
            style={{ width: '100%' }}
            value={extraAction}
            onChange={e => {
              setExtraAction(e.target.value);
            }}
          >
            <Radio value={9}>
              {translate('steps.contract-suspend.description')}
            </Radio>
            <div style={{ padding: '10px' }}>
              <Select
                onChange={value => {
                  setSelectedSuspension(value);
                }}
                options={suspensionOptions}
                value={selectedSuspension}
                style={{
                  width: '100%',
                }}
                disabled={extraAction === 10}
              />
            </div>
            <Alert
              showIcon
              description={translate(
                'steps.contract-suspend.suspension-description',
              )}
              style={{ marginTop: 8, marginBottom: 16 }}
            />

            <Radio value={10} style={{ marginTop: '10px' }}>
              {translate('steps.contract-suspend.description2')}
            </Radio>

            <div style={{ padding: '10px', width: '100%' }}>
              <DatePicker
                value={dateTimeToRemoveSuspension}
                disabled={extraAction === 9}
                format="DD/MM/YYYY"
                disabledDate={disabledDate}
                style={{
                  width: '100%',
                }}
                allowClear={false}
                onChange={event => {
                  if (
                    event &&
                    event.isSame(moment(), 'day') &&
                    event.isBefore(moment())
                  ) {
                    setDateTimeToRemoveSuspension(moment().add(1, 'minute'));
                  } else {
                    setDateTimeToRemoveSuspension(event);
                  }
                }}
              />
            </div>
          </Radio.Group>
        </div>
      ),
    },
  ];

  const reinitState = () => {
    setSelectedSuspension(30);
    setDateTimeToRemoveSuspension(moment().add(1, 'hour'));
    setExtraAction(9);
  };

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    const formatedDate = moment(dateTimeToRemoveSuspension).format(
      'YYYY-MM-DD',
    );
    payload.action = extraAction;

    payload.devices =
      devicesProps && devicesProps?.length > 0
        ? devicesProps?.map(device => {
            return { msisdn: device.msisdn };
          })
        : uploadedDevices;

    // extraAction === 9 -> radio suspend
    // extraAction === 10 -> radio remove
    payload.suspensionDays =
      extraAction === 9 ? selectedSuspension : formatedDate;

    return handleDeviceActions(payload);
  }, [
    devicesProps,
    selectedSuspension,
    extraAction,
    uploadedDevices,
    handleDeviceActions,
    dateTimeToRemoveSuspension,
  ]);

  const suspensionData = {
    contentSteps,
    onSubmit,
    reinitState,
    extraAction,
  };

  return { suspensionData };
};

export default useActionSuspension;
